import { useId, useState } from 'react';
import { cn } from '@tsgecart/ui';
import type { DashboardDatePreset } from '../../types/dashboard.js';

const PRESET_LABELS: Record<Exclude<DashboardDatePreset, 'custom'>, string> = {
  today: 'Today',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
};

export interface AdminDateRangeSelectorProps {
  preset: DashboardDatePreset;
  from?: string;
  to?: string;
  onChange: (value: { preset: DashboardDatePreset; from?: string; to?: string }) => void;
  className?: string;
}

/**
 * Every boundary this control can produce is re-validated server-side
 * (dashboard-query-schema.ts) regardless of what's selected here -- this
 * is a UX convenience for building the query string, not a trust boundary.
 * All dates are handled as plain `YYYY-MM-DD` local-date-picker values;
 * the Worker interprets `from`/`to` as UTC instants (see
 * docs/admin-analytics-definitions.md "Date-range rules") and the
 * resulting period boundaries are rendered back by the caller in the
 * viewer's local time, never assumed here.
 */
export function AdminDateRangeSelector({ preset, from, to, onChange, className }: AdminDateRangeSelectorProps) {
  const [showCustom, setShowCustom] = useState(preset === 'custom');
  const fromId = useId();
  const toId = useId();

  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)} role="group" aria-label="Date range">
      {(Object.keys(PRESET_LABELS) as Array<keyof typeof PRESET_LABELS>).map((key) => (
        <button
          key={key}
          type="button"
          aria-pressed={preset === key}
          onClick={() => {
            setShowCustom(false);
            onChange({ preset: key });
          }}
          className={cn(
            'focus-visible:ring-brand rounded-full px-3 py-1.5 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
            preset === key ? 'bg-brand text-brand-foreground' : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
          )}
        >
          {PRESET_LABELS[key]}
        </button>
      ))}
      <button
        type="button"
        aria-pressed={preset === 'custom'}
        onClick={() => setShowCustom((value) => !value)}
        className={cn(
          'focus-visible:ring-brand rounded-full px-3 py-1.5 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
          preset === 'custom' ? 'bg-brand text-brand-foreground' : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
        )}
      >
        Custom
      </button>

      {showCustom && (
        <div className="flex items-center gap-2">
          <label htmlFor={fromId} className="sr-only">
            Start date
          </label>
          <input
            id={fromId}
            type="date"
            defaultValue={from}
            className="focus-visible:ring-brand rounded-lg border border-gray-200 px-2 py-1 text-sm focus-visible:outline-none focus-visible:ring-2"
            onChange={(event) => {
              const value = event.target.value;
              if (value && to) onChange({ preset: 'custom', from: value, to });
            }}
          />
          <span className="text-sm text-gray-400" aria-hidden="true">
            –
          </span>
          <label htmlFor={toId} className="sr-only">
            End date
          </label>
          <input
            id={toId}
            type="date"
            defaultValue={to}
            className="focus-visible:ring-brand rounded-lg border border-gray-200 px-2 py-1 text-sm focus-visible:outline-none focus-visible:ring-2"
            onChange={(event) => {
              const value = event.target.value;
              if (from && value) onChange({ preset: 'custom', from, to: value });
            }}
          />
        </div>
      )}
    </div>
  );
}
